export interface AnalyticsAttribute {
  name: string;
  type: 'value' | 'list' | 'map' | 'custom';
  definition?: string;
  data_type?: string;
  attributes: AnalyticsAttribute[] | null;
  custom_attribute_logic?: string;
}

export interface AttributeSection {
  analytics_attributes: AnalyticsAttribute[];
}

export interface AttributeCategory {
  [timeframe: string]: AttributeSection;
}

export interface AnalyticsAttributesResponse {
  overall_analysis: {
    [sectionName: string]: AttributeCategory;
  };
  account_wise_analysis: {
    [sectionName: string]: AttributeCategory;
  };
}

export interface BusinessRegistrationResult {
  registration: {
    status: string;
    businessId: string;
    businessStatus: string;
    message?: string;
  };
  integration: {
    status: string;
    businessId: string;
    clientId: string;
    clientSecret: string;
    apiKey?: string;
    scopes: string[];
    message?: string;
  };
}

export interface JourneyWorkflowConfig {
  workflow_configuration: Record<string, any>;
  workflow_id: string;
}

export interface ConfiguredJourney {
  journey_id: string;
  journey_name: string;
  journey_sub_text: string;
  journey_type: string;
  journey_workflow_config: JourneyWorkflowConfig;
}

export interface AnalyticsConfigRequest {
  gateway_instance_id: string;
  business_id: string;
  client_id: string;
  configured_journeys: ConfiguredJourney[];
  header_name: string;
}

export interface ClientAnalyticsConfig {
  analytics_config: any | null;
  exchange_application_id: string | null;
  finpro_base_url?: string;
  fiu_creds?: {
    organisationId: string;
    client_id: string;
    client_secret: string;
    appIdentifier: string;
  } | null;
}

export interface ConfiguredJourneyInfo {
  journey_id: string;
  journey_name: string;
  journey_sub_text: string;
  journey_type: string;
  journey_workflow_config: {
    workflow_id: string;
    workflow_configuration: {
      analytics_configuration: {
        analytics_configuration_type: string;
        [key: string]: any;
      };
    };
  };
}

export enum OnboardingStep {
  LANDING = 0,
  REGISTER = 1,
  CONFIGURE = 2,
  COMPLETE = 3,
}

export const WORKFLOW_IDS = {
  CONSENT: 'NOVA_WORKFLOW:NOVA_EQUALITICS_WORKFLOW',
  PDF: 'NOVA_WORKFLOW:NOVA_BANK_STATEMENT_OCR_WORKFLOW',
};

export const JOURNEY_TEMPLATES = {
  EXCEL_STANDARD: {
    journey_name: "EQUALITICS",
    journey_sub_text: "Equalitics",
    journey_type: "EQUALITICS",
    journey_workflow_config: {
      workflow_configuration: {
        analytics_configuration: { analytics_configuration_type: "V3" },
        report_configuration: { excel_template: "STANDARD" }
      },
      workflow_id: WORKFLOW_IDS.CONSENT
    }
  },
  JSON_STANDARD: {
    journey_name: "EQUALITICS",
    journey_sub_text: "Equalitics",
    journey_type: "EQUALITICS",
    journey_workflow_config: {
      workflow_configuration: {
        analytics_configuration: {
          analytics_configuration_type: "V2",
          response_mapping_expression: "bankingAnalysis"
        }
      },
      workflow_id: WORKFLOW_IDS.CONSENT
    }
  },
  DYNAMIC_ATTRIBUTES: {
    journey_name: "EQUALITICS",
    journey_sub_text: "Equalitics",
    journey_type: "EQUALITICS",
    journey_workflow_config: {
      workflow_configuration: {
        analytics_configuration: {
          analytics_attributes_configuration: {},
          analytics_configuration_type: "V2"
        }
      },
      workflow_id: WORKFLOW_IDS.CONSENT
    }
  },
  // PDF variants — same config types but with bank statement OCR workflow
  EXCEL_STANDARD_PDF: {
    journey_name: "BANK_STATEMENT_OCR",
    journey_sub_text: "Verify Any Bank Statement",
    journey_type: "BANK_STATEMENT_OCR",
    journey_workflow_config: {
      workflow_configuration: {
        analytics_configuration: { analytics_configuration_type: "V3" },
        report_configuration: { excel_template: "STANDARD" }
      },
      workflow_id: WORKFLOW_IDS.PDF
    }
  },
  JSON_STANDARD_PDF: {
    journey_name: "BANK_STATEMENT_OCR",
    journey_sub_text: "Verify Any Bank Statement",
    journey_type: "BANK_STATEMENT_OCR",
    journey_workflow_config: {
      workflow_configuration: {
        analytics_configuration: {
          analytics_configuration_type: "V2",
          response_mapping_expression: "bankingAnalysis"
        }
      },
      workflow_id: WORKFLOW_IDS.PDF
    }
  },
  DYNAMIC_ATTRIBUTES_PDF: {
    journey_name: "BANK_STATEMENT_OCR",
    journey_sub_text: "Verify Any Bank Statement",
    journey_type: "BANK_STATEMENT_OCR",
    journey_workflow_config: {
      workflow_configuration: {
        analytics_configuration: {
          analytics_attributes_configuration: {},
          analytics_configuration_type: "V2"
        }
      },
      workflow_id: WORKFLOW_IDS.PDF
    }
  }
};
